import { useEffect, useState } from 'react';
import { generateUniqueId } from '../../utils/common';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { docxSubitemConfigSchema } from '../../form-schemas/subitemFilterSchema';

/**
 * // SubItemConfiguration Schema
tableId: String // uniqueId of subitem table
selectedColumns: [] string // columnIds of all the subitem columns selected for table.
sum:{
    columnId: string, // Denotes which column values should be summed.
    targetId: string // Unique Placeholder tag for Sum of the target subitem configuration
}
tax:{
    targetId: string // Unique Placeholder tag for Tax of the target subitem configuration
    type: string // Identifies if the Tax is percentage or fixed.
    value: number // Tax value
}
customizedUnit:{
    columnId: string // target column where the customized unit that will be shown.
    type: string // Specifies if the unit is to be shown before or after
}
*/

const DEFAULT = (fileId) => ({
  tableId: '',
  direction: 'ltr',
  selectedColumns: [{ id: 'item-name', label: 'Item' }],
  fileId,
  sum: {
    // Optional
    column: null,
    targetId: '',
    label: 'Sum',
    is_shown: false,
  },
  tax: {
    // Optional
    targetId: '',
    type: null,
    value: 0,
    label: 'Tax',
    is_shown: false,
  },
  customizedUnit: {
    // Optional
    targetId: '',
    value: null,
    position: null,
    is_shown: false,
    columns: [],
  },
  header: {
    is_shown: false,
    color: '',
    targetId: '',
    rgb: '',
  },
  isFilterEnabled: false,
  filterChaining: '',
  filters: [],
});

const useCkSubitems = ({
  editorRef,
  mode = 'create',
  subItemConfigurations,
  fileId,
  defaultValue,
  onSaveCallback = (mode, tableConfig) => {},
}) => {
  const filterFormMethods = useForm({
    resolver: yupResolver(docxSubitemConfigSchema),
    defaultValues: DEFAULT(fileId || ''),
  });

  const handleSaveConfig = (data) => {
    if (mode === 'create') {
      // Get new Table ID

      const tableIDs = new Set();

      for (let i = 0; i < subItemConfigurations.length - 1; i++) {
        tableIDs.add(subItemConfigurations[i].tableId);
      }

      const tableId = generateUniqueId(tableIDs);

      const newSubItemConfig = {
        ...data,
        tableId,
        direction: data.direction || 'ltr',
      };
      // Generate HTML string for the subitem

      const sumTargetId = `${tableId}#sum`;

      const taxTargetId = `${tableId}#tax`;

      // Insert the HTML string into the editor
      // Update the editor state
      // Call Create Subitem API
      // Update react query cache
      // Close the modal

      const editor = editorRef.current;

      newSubItemConfig.sum.targetId = sumTargetId;

      newSubItemConfig.tax.targetId = taxTargetId;

      // Define the table rows and columns

      onSaveCallback(mode, newSubItemConfig);

      editor.execute('insertDynamicTable', { subItemConfig: newSubItemConfig });
    }

    if (mode === 'edit') {
      // Preserve the existing tableId when editing
      const tableId = defaultValue?.tableId || data?.tableId;
      if (!tableId) {
        console.error('SuperDoc: Cannot edit table - no tableId found');
        return;
      }

      const newSubItemConfig = {
        ...data,
        tableId,
        direction: defaultValue?.direction || data.direction || 'ltr',
      };
      // Generate HTML string for the subitem

      const sumTargetId = `${tableId}#sum`;

      const taxTargetId = `${tableId}#tax`;

      // Insert the HTML string into the editor
      // Update the editor state
      // Call Create Subitem API
      // Update react query cache
      // Close the modal

      const editor = editorRef.current;

      newSubItemConfig.sum.targetId = sumTargetId;

      newSubItemConfig.tax.targetId = taxTargetId;

      onSaveCallback(mode, newSubItemConfig);

      editor.execute('insertDynamicTable', { subItemConfig: newSubItemConfig });
    }
  };

  const { append, remove, update } = useFieldArray({
    control: filterFormMethods.control,
    name: 'filters',
  });

  const updateFilters = (action, payload) => {
    switch (action) {
      case 'add':
        append(payload);
        break;
      case 'remove':
        remove(payload);
        break;
      case 'update':
        const { index, filter } = payload;
        update(index, filter);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const base = DEFAULT(fileId || '');
    if (defaultValue) {
      const merged = { ...base, ...defaultValue };
      filterFormMethods.reset(merged);
    } else {
      filterFormMethods.reset(base);
    }
  }, [defaultValue, fileId]);

  return {
    handleSaveConfig,
    filterFormMethods,
    updateFilters,
  };
};

export default useCkSubitems;
